const GRADE_COLOR_SCALE = ['#E02727', '#FF9238', '#FFB55A', '#49A8F4', '#00C0B9', '#1FC24E'];

const DEFAULT_COLOR = '#54B0F8';
const DEFAULT_BG = 'rgba(148, 163, 184, 0.12)';

function clampRatio(ratio) {
  return Math.min(1, Math.max(0, ratio));
}

function getColorForRatio(ratio) {
  const safeRatio = clampRatio(ratio);

  if (GRADE_COLOR_SCALE.length <= 1) {
    return GRADE_COLOR_SCALE[0] || DEFAULT_COLOR;
  }

  const position = safeRatio * (GRADE_COLOR_SCALE.length - 1);
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.min(lowerIndex + 1, GRADE_COLOR_SCALE.length - 1);

  if (lowerIndex === upperIndex) {
    return GRADE_COLOR_SCALE[lowerIndex];
  }

  return GRADE_COLOR_SCALE[Math.round(position)];
}

export function normalizeDisciplineSystem(disciplineSystem) {
  if (!disciplineSystem) {
    return null;
  }

  return {
    gradingSystemType: disciplineSystem.disciplineSystemType,
    qualitativeLevels: disciplineSystem.disciplineQualitativeLevels || [],
    numericMin: disciplineSystem.disciplineNumericMin,
    numericMax: disciplineSystem.disciplineNumericMax,
  };
}

export function isQualitativeGrading(gradingSystem) {
  return gradingSystem?.gradingSystemType === 'qualitative';
}

export function isNumericGrading(gradingSystem) {
  return gradingSystem?.gradingSystemType === 'numeric';
}

export function getOrderedQualitativeLevels(gradingSystem) {
  if (!isQualitativeGrading(gradingSystem)) {
    return [];
  }

  return [...(gradingSystem.qualitativeLevels || [])].sort((left, right) => left.order - right.order);
}

export function getLadderQualitativeLevels(gradingSystem) {
  return [...getOrderedQualitativeLevels(gradingSystem)].reverse();
}

function findQualitativeLevel(value, gradingSystem) {
  if (!value || !isQualitativeGrading(gradingSystem)) {
    return null;
  }

  const normalizedValue = String(value);

  return (
    gradingSystem.qualitativeLevels?.find(
      (level) => level.key === normalizedValue || level.title === normalizedValue
    ) || null
  );
}

export function resolveGradeLabel(value, label, gradingSystem) {
  if (label) {
    return label;
  }

  if (value == null || value === '') {
    return 'نامشخص';
  }

  if (isQualitativeGrading(gradingSystem)) {
    return findQualitativeLevel(value, gradingSystem)?.title || String(value);
  }

  return String(value);
}

export function getGradeRatio(value, gradingSystem) {
  if (!gradingSystem || value == null || value === '') {
    return null;
  }

  if (isQualitativeGrading(gradingSystem)) {
    const orderedLevels = getOrderedQualitativeLevels(gradingSystem);

    if (orderedLevels.length === 0) {
      return null;
    }

    const matchedLevel = findQualitativeLevel(value, gradingSystem);

    if (!matchedLevel) {
      return null;
    }

    if (orderedLevels.length === 1) {
      return 1;
    }

    const index = orderedLevels.findIndex((level) => level.key === matchedLevel.key);

    return index / (orderedLevels.length - 1);
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return null;
  }

  const min = Number(gradingSystem.numericMin);
  const max = Number(gradingSystem.numericMax);

  if (Number.isNaN(min) || Number.isNaN(max) || max <= min) {
    return null;
  }

  return clampRatio((numericValue - min) / (max - min));
}

export function getGradeColor(value, gradingSystem) {
  const ratio = getGradeRatio(value, gradingSystem);

  if (ratio == null) {
    return DEFAULT_COLOR;
  }

  return getColorForRatio(ratio);
}

export function getGradeBackground(value, gradingSystem) {
  const color = getGradeColor(value, gradingSystem);
  const rgb = hexToRgb(color);

  if (!rgb) {
    return DEFAULT_BG;
  }

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`;
}

export function getGradeLadderHeight(index, total, minHeight = 28, maxHeight = 76) {
  if (total <= 1) {
    return `${maxHeight}px`;
  }

  const ratio = index / (total - 1);

  return `${Math.round(minHeight + ratio * (maxHeight - minHeight))}px`;
}

export function getGradeMeterWidth(value, gradingSystem, minWidth = 28, maxWidth = 100) {
  const ratio = getGradeRatio(value, gradingSystem);

  if (ratio == null) {
    return '55%';
  }

  return `${Math.round(minWidth + ratio * (maxWidth - minWidth))}%`;
}

export function isGradeMatch(currentValue, levelKey, gradingSystem) {
  if (currentValue == null || currentValue === '' || levelKey == null || levelKey === '') {
    return false;
  }

  if (isQualitativeGrading(gradingSystem)) {
    const matchedLevel = findQualitativeLevel(currentValue, gradingSystem);

    return matchedLevel?.key === levelKey;
  }

  return String(currentValue) === String(levelKey);
}

export function getLadderItems(gradingSystem) {
  const levels = getLadderQualitativeLevels(gradingSystem);

  return levels.map((level, index) => ({
    key: level.key,
    title: level.title,
    order: level.order,
    color: getColorForRatio(levels.length <= 1 ? 1 : index / (levels.length - 1)),
    height: getGradeLadderHeight(index, levels.length),
  }));
}

function hexToRgb(hex) {
  const normalized = hex.replace('#', '');

  if (normalized.length !== 6) {
    return null;
  }

  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}
